import { useRef, useEffect, useState } from "react";
import SignaturePad from "signature_pad";
import { Pencil, Upload, Eraser, Loader2 } from "lucide-react";
import { compressImageToDataUri } from "@/utils/imageCompress";

const SIG_MAX_BYTES = 150 * 1024; // matches the backend signature cap

/**
 * Capture a signature by drawing it or uploading a photo of one. Emits a base64
 * data URI (transparent PNG for drawings, compressed PNG/JPEG for uploads) via
 * onChange, or null when cleared.
 */
export function SignatureInput({ value, onChange }) {
  const [mode, setMode] = useState("draw");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const canvasRef = useRef(null);
  const padRef = useRef(null);
  const fileRef = useRef(null);

  // Init/teardown SignaturePad when the draw tab is active.
  useEffect(() => {
    if (mode !== "draw" || !canvasRef.current) return;
    const canvas = canvasRef.current;
    // Crisp lines on retina: size the backing store to the device pixel ratio.
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    const pad = new SignaturePad(canvas, { penColor: "#0f172a", minWidth: 0.7, maxWidth: 2.2 });
    pad.addEventListener("endStroke", () => {
      if (!pad.isEmpty()) onChange(pad.toDataURL("image/png"));
    });
    padRef.current = pad;
    return () => { pad.off(); padRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const clearDraw = () => {
    padRef.current?.clear();
    onChange(null);
  };

  const handleUpload = async (file) => {
    setError("");
    if (!file) return;
    setBusy(true);
    try {
      onChange(await compressImageToDataUri(file, { maxBytes: SIG_MAX_BYTES }));
    } catch (err) {
      setError(err.message || "Could not process that image.");
    } finally {
      setBusy(false);
    }
  };

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button
      type="button"
      onClick={() => setMode(id)}
      className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ${
        mode === id ? "bg-brand-600 text-white" : "bg-card border border-border hover:bg-accent"
      }`}
    >
      <Icon size={15} /> {label}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <TabBtn id="draw" icon={Pencil} label="Draw" />
        <TabBtn id="upload" icon={Upload} label="Upload a photo" />
      </div>

      {mode === "draw" ? (
        <div className="space-y-2">
          <canvas
            ref={canvasRef}
            className="h-40 w-full touch-none rounded-lg border border-border bg-white"
          />
          <button
            type="button"
            onClick={clearDraw}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <Eraser size={14} /> Clear
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
          <button
            type="button"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {busy ? "Optimizing…" : "Choose a photo of your signature"}
          </button>
          <p className="text-xs text-muted-foreground">Any image works — we resize it automatically.</p>
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}

      {value && (
        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="mb-1 text-xs font-medium text-muted-foreground">Current signature</p>
          <img src={value} alt="signature preview" className="max-h-20 object-contain" />
        </div>
      )}
    </div>
  );
}
